import { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppContext from '../services/context';
import PageTemplate from '../components/PageTemplate';
import { usePageTitle } from '../hooks/usePageTitle.js';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import DeleteAccountModal from '../components/DeleteAccountModal';
import { contactAreas, formatPhoneBR } from '../hooks/useUserProfile';
import { useError } from '../hooks/useError.js';
import { useToast } from '../hooks/useToast.js';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';

export default function UserSettings() {
  usePageTitle('Configurações');
  const {
    userState,
    userActions,
    themeKey,
    toggleTheme,
    semanticSearchEnabled,
    toggleSemanticSearch,
    setIsAiMode,
  } = useContext(AppContext);
  const { showError } = useError();
  const toast = useToast();
  const navigate = useNavigate();
  const { hash } = useLocation();

  const { profile, isAuthenticated, contributionCount } = userState;
  const aiModePreferenceEnabled = profile?.preferences?.aiMode === true;
  
  const [editingContact, setEditingContact] = useState(null); // 'email' | 'phone'
  const [newContactValue, setNewContactValue] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [step, setStep] = useState('idle'); // idle, input-contact, input-pin
  const [message, setMessage] = useState({ text: '', variant: 'success' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      fullName: '',
      birthDate: '',
      area: '',
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required('Nome é obrigatório'),
      birthDate: Yup.date().required('Data de nascimento é obrigatória'),
      area: Yup.string().required('Área de atuação é obrigatória'),
    }),
    onSubmit: async (values) => {
      const res = await userActions.updateProfile(values);
      if (res.success) {
        const successMessage = 'Perfil atualizado com sucesso!';
        setMessage({ text: successMessage, variant: 'success' });
        toast.success(successMessage);
      } else {
        setMessage({ text: showError(res.message || 'Erro ao atualizar perfil.'), variant: 'error' });
      }
    }
  });
  const { setValues } = formik;

  useEffect(() => {
    if (profile) {
      setValues({
        fullName: profile.fullName || '',
        birthDate: profile.birthDate || '',
        area: profile.area || '',
      });
    }
  }, [profile, setValues]);

  // Âncoras por seção (/configuracoes#excluir-conta). O React Router não rola
  // para o hash sozinho, e o alvo só existe depois que o perfil autentica a
  // página — por isso o efeito depende de isAuthenticated.
  //
  // A rolagem é instantânea e reaplicada no 'load': imagens do cabeçalho e do
  // rodapé ainda mudam a altura do documento depois do primeiro paint, e uma
  // rolagem suave iniciada antes disso mira uma posição que deixa de valer.
  useEffect(() => {
    if (!hash || !isAuthenticated) return undefined;

    const scrollToTarget = () => {
      document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
    };

    const frame = requestAnimationFrame(scrollToTarget);
    if (document.readyState === 'complete') {
      return () => cancelAnimationFrame(frame);
    }

    window.addEventListener('load', scrollToTarget);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('load', scrollToTarget);
    };
  }, [hash, isAuthenticated]);

  const startEditContact = (channel) => {
    setEditingContact(channel);
    setNewContactValue('');
    setPinCode('');
    setStep('input-contact');
    setMessage({ text: '', variant: 'success' });
  };

  const handleRequestPin = async () => {
    if (!newContactValue) return;
    const res = await userActions.requestPin(editingContact, newContactValue);
    if (res.success) {
      setStep('input-pin');
      const successMessage = `PIN enviado para ${newContactValue}`;
      setMessage({ text: successMessage, variant: 'success' });
      toast.success(successMessage);
    } else {
      setMessage({ text: showError(res.message || 'Erro ao enviar PIN.'), variant: 'error' });
    }
  };

  const handleVerifyPin = async () => {
    const res = await userActions.verifyPin(editingContact, pinCode);
    if (res.success) {
      const successMessage = 'Contato atualizado com sucesso!';
      setMessage({ text: successMessage, variant: 'success' });
      toast.success(successMessage);
      setStep('idle');
      setEditingContact(null);
    } else {
      setMessage({ text: showError(res.message || 'Erro ao verificar PIN.'), variant: 'error' });
    }
  };

  const handleToggleAiPreference = async () => {
    const nextValue = !aiModePreferenceEnabled;
    const nextPreferences = {
      ...(profile?.preferences || {}),
      aiMode: nextValue
    };

    const result = await userActions.updateProfile({ preferences: nextPreferences });
    if (!result.success) {
      setMessage({ text: showError(result.message || 'Erro ao atualizar preferência de IA.'), variant: 'error' });
      return;
    }

    if (!nextValue) {
      setIsAiMode(false);
    }
    const successMessage = 'Preferência de IA atualizada.';
    setMessage({ text: successMessage, variant: 'success' });
    toast.success(successMessage);
  };

  const handleDeleteAccount = async (confirmation) => {
    const result = await userActions.deleteAccount(confirmation);
    if (result.success) {
      setIsDeleteModalOpen(false);
      toast.success(result.message || 'Conta excluída.');
      navigate('/');
    }
    return result;
  };

  if (!isAuthenticated) {
    return (
       <PageTemplate showSearchInput={false} alwaysShowLogo={true}>
         <div className="max-w-2xl mx-auto p-8 text-center">
           <p>Você precisa estar logado para acessar as configurações.</p>
         </div>
       </PageTemplate>
    );
  }

  return (
    <PageTemplate showSearchInput={false} alwaysShowLogo={true}>
      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-8">
        <h1 className="text-2xl font-bold mb-6">Configurações</h1>

        {message.text && (
          <div
            className={clsx(
              'p-4 rounded mb-4',
              message.variant === 'error'
                ? 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-100'
                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
            )}
          >
            {message.text}
          </div>
        )}

        {/* Profile Section */}
        <section id="dados-pessoais" aria-labelledby="dados-pessoais-titulo" className="scroll-mt-6 bg-white dark:bg-gray-800 p-6 rounded-lg shadow space-y-4">
          <h2 id="dados-pessoais-titulo" className="text-xl font-semibold mb-4">Dados Pessoais</h2>
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                name="fullName"
                label="Nome Completo"
                formik={formik}
              />
              <Input
                name="birthDate"
                label="Data de Nascimento"
                type="date"
                formik={formik}
              />
              <div className="md:col-span-2">
                 <Select
                   name="area"
                   label="Área de Atuação"
                   options={contactAreas.map(a => ({ value: a, label: a }))}
                   formik={formik}
                 />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button type="submit">Salvar Alterações</Button>
            </div>
          </form>
        </section>

        {/* Contact Section */}
        <section id="contatos" aria-labelledby="contatos-titulo" className="scroll-mt-6 bg-white dark:bg-gray-800 p-6 rounded-lg shadow space-y-4">
          <h2 id="contatos-titulo" className="text-xl font-semibold mb-4">Contatos</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">E-mail</label>
              <div className="flex items-center justify-between p-2 border rounded dark:border-gray-600">
                <span>{profile.email}</span>
                <button onClick={() => startEditContact('email')} className="text-blue-500 hover:underline text-sm">Alterar</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone</label>
              <div className="flex items-center justify-between p-2 border rounded dark:border-gray-600">
                <span>{profile.phone}</span>
                <button onClick={() => startEditContact('phone')} className="text-blue-500 hover:underline text-sm">Alterar</button>
              </div>
            </div>
          </div>

          {step !== 'idle' && (
            <div className="mt-4 p-4 border border-blue-200 bg-blue-50 dark:bg-blue-900/20 rounded">
              <h3 className="font-semibold mb-2">Alterar {editingContact === 'email' ? 'E-mail' : 'Telefone'}</h3>
              
              {step === 'input-contact' && (
                <div className="flex flex-col md:flex-row gap-2">
                   {/* Using standard input here for simplicity in this sub-form */}
                   <input 
                      className="border p-2 rounded dark:bg-gray-700 dark:border-gray-600"
                      placeholder={editingContact === 'email' ? 'Novo e-mail' : '(71) 99999-9999'}
                      type={editingContact === 'email' ? 'email' : 'tel'}
                      inputMode={editingContact === 'email' ? 'email' : 'numeric'}
                      maxLength={editingContact === 'email' ? undefined : 16}
                      value={newContactValue}
                      onChange={(e) => setNewContactValue(
                        editingContact === 'phone' ? formatPhoneBR(e.target.value) : e.target.value
                      )}
                   />
                  <div className="flex gap-2">
                    <Button onClick={handleRequestPin}>Enviar PIN</Button>
                    <Button className="secondary" onClick={() => setStep('idle')}>Cancelar</Button>
                  </div>
                </div>
              )}

              {step === 'input-pin' && (
                <div className="flex flex-col md:flex-row gap-2">
                   <input 
                      className="border p-2 rounded dark:bg-gray-700 dark:border-gray-600"
                      placeholder="Código PIN"
                      value={pinCode}
                      onChange={(e) => setPinCode(e.target.value)}
                   />
                  <div className="flex gap-2">
                    <Button onClick={handleVerifyPin}>Confirmar</Button>
                    <Button className="secondary" onClick={() => setStep('idle')}>Cancelar</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Preferences Section */}
        <section id="preferencias" aria-labelledby="preferencias-titulo" className="scroll-mt-6 bg-white dark:bg-gray-800 p-6 rounded-lg shadow space-y-4">
          <h2 id="preferencias-titulo" className="text-xl font-semibold mb-4">Preferências do Sistema</h2>
          
          <div className="flex items-center justify-between py-2 border-b dark:border-gray-700">
            <div>
              <span className="block font-medium">Tema Escuro</span>
              <span className="text-sm text-gray-500">Alternar entre tema claro e escuro</span>
            </div>
            <div 
              className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${themeKey === 'dark' ? 'bg-green-500' : 'bg-gray-300'}`}
              onClick={toggleTheme}
            >
              <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${themeKey === 'dark' ? 'translate-x-6' : ''}`} />
            </div>
          </div>

          <div className="flex items-center justify-between py-2 border-b dark:border-gray-700">
            <div>
              <span className="block font-medium">Busca Semântica (Reposiórios)</span>
              <span className="text-sm text-gray-500">Habilitar busca em bases externas</span>
            </div>
            <div 
              className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${semanticSearchEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              onClick={toggleSemanticSearch}
            >
              <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${semanticSearchEnabled ? 'translate-x-6' : ''}`} />
            </div>
          </div>

          <div className="flex items-center justify-between py-2">
             <div>
              <span className="block font-medium">Modo IA</span>
              <span className="text-sm text-gray-500">Usar inteligência artificial para resumir resultados</span>
            </div>
            <div 
              className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${aiModePreferenceEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              onClick={handleToggleAiPreference}
            >
              <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${aiModePreferenceEnabled ? 'translate-x-6' : ''}`} />
            </div>
          </div>

        </section>

        {/* Danger Zone */}
        <section id="excluir-conta" aria-labelledby="excluir-conta-titulo" className="scroll-mt-6 border-2 border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/30 p-6 rounded-lg space-y-4">
          <h2 id="excluir-conta-titulo" className="text-xl font-semibold text-red-800 dark:text-red-200">Danger Zone</h2>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <span className="block font-medium text-red-900 dark:text-red-100">Excluir conta</span>
              <span className="text-sm text-red-800/80 dark:text-red-200/80">
                Remove seus dados de cadastro, contatos e contribuições em análise. As contribuições
                já aprovadas permanecem no acervo sem vínculo com você. Não é possível reverter.
              </span>
            </div>
            <Button
              className="!bg-red-700 hover:enabled:!bg-red-600 shrink-0"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              Excluir conta
            </Button>
          </div>
        </section>
      </div>

      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        contributionCount={contributionCount}
      />
    </PageTemplate>
  );
}
